import './CommandButtonComponent.css';

import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";

import { useCurrentCommandState } from "../../store/currentCommandStateStore";
import { emitter } from "../../input/AppEvents";
import CommandIdEnum from "../../enums/CommandIdEnums";
import { useDocumentsStateStore } from '../../store/documentsStateStore';
import { useServicesState } from '../../store/servicesStateStore';
import hasRequiredRights from '../../commands/commandUserRights';

interface CommandButtonComponentProps {
    commandId: CommandIdEnum;
    commandNameResourceKey: string;
    commandIconPath: string;
}

enum CommandButtonStateEnum {
    Enabled,
    Running,
    DisabledDueToActiveCommand,
    DisabledDueToRights
}

const CommandButtonComponent = (props: CommandButtonComponentProps) => {  
    const currentCommand = useCurrentCommandState((state) => state.commandId);
    const activeDocumentIndex = useDocumentsStateStore(state => state.activeDocumentIndex);
    const { t } = useTranslation();

    const { commandId, commandNameResourceKey, commandIconPath } = props;

    const isCurrentCommand = currentCommand === commandId;

    const [cmdState, setCmdState] = useState<CommandButtonStateEnum>(CommandButtonStateEnum.Enabled);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [tooltip, setTooltip] = useState<string>(t(commandNameResourceKey));
    const [className, setClassName] = useState<string>("command-button");

    const handleCommandLaunch = () => {
        if (isCurrentCommand) // If we are already running this command, we can use the button to cancel it
            emitter.emit("commandStop", undefined);
        else
            emitter.emit("commandLaunch", { commandId });
    };

    const onCommandStateChange = (newState: CommandButtonStateEnum) => {
        switch (newState) {
            case CommandButtonStateEnum.Enabled:
                setIsDisabled(false);
                setTooltip(t(commandNameResourceKey));
                setClassName("command-button");
                break;
            case CommandButtonStateEnum.Running:
                setIsDisabled(false);
                setClassName("command-button command-button-active");
                setTooltip(t("ui.commandButton.clickToCancelCommand"));
                break;
            case CommandButtonStateEnum.DisabledDueToActiveCommand:
                setIsDisabled(true);
                setClassName("command-button command-button-disabled");
                setTooltip(t("ui.commandButton.disabledDueToActiveCommand"));
                break;
            case CommandButtonStateEnum.DisabledDueToRights:
                setIsDisabled(true);
                setClassName("command-button command-button-not-allowed");
                setTooltip(t("ui.commandButton.disableDueToRights"));
                break;
        }
    };

    useEffect(() => {
        onCommandStateChange(cmdState);
    }, [cmdState]);

    useEffect(() => {
        if (activeDocumentIndex >= 0)
        {
            const currentDocument = useDocumentsStateStore.getState().getActiveDocument();

            const disabledByRights = currentDocument 
                            ? !hasRequiredRights(currentDocument.document.userRight, commandId)
                            : true;

            const currentCmdInstance = useServicesState.getState().commandStack.getCurrentCmd();
            const disabledByActiveCommand = currentCmdInstance !== null && !currentCmdInstance.canLaunch(commandId);

            if (disabledByRights) {
                console.warn(`Command ${CommandIdEnum[commandId]} is disabled due to `
                                + currentDocument 
                                ? `current user right ${currentDocument!.document.userRight}`
                                : `no active document`
                );
                setCmdState(CommandButtonStateEnum.DisabledDueToRights);
            }
            else if (disabledByActiveCommand) {
                if (isCurrentCommand) {
                    setCmdState(CommandButtonStateEnum.Running);
                }
                else {
                    setCmdState(CommandButtonStateEnum.DisabledDueToActiveCommand);
                }
            }
            else {
                setCmdState(CommandButtonStateEnum.Enabled);
            }
        }
        else {
            setCmdState(CommandButtonStateEnum.Enabled);
        }
    }, [activeDocumentIndex, currentCommand, commandId]);

    return (
        <button className={className}
                title={tooltip}
                disabled={isDisabled}
                onClick={handleCommandLaunch}>
            <img    src={commandIconPath}
                    alt={tooltip}
                    style={{ width: '24px', height: '24px' }} />
        </button>
    );
}
export default CommandButtonComponent;